import React from 'react';
import { ChoiceGroup, Dropdown, IDropdownOption, Stack } from '@fluentui/react';
import NumberField from '../Inputs/NumberField';
import {
    PROMO_MARKUP_DISCOUNT, PROMO_MARKUP_GROSS_MARGIN, PROMO_MARKUP_PERCENT, isDiscountPromo,
} from '../../models/ProductPromo';

interface IProps {
    markupType?: string;
    markup?: number;
    /** Both together: the mode and its number are one decision, and half of it is not a valid state. */
    onChange: (markupType: string | undefined, markup: number | undefined) => void;
    /**
     * Adds an "inherit" choice, for the per-product override where an unset value means "whatever the
     * campaign says" rather than a value of its own.
     */
    allowInherit?: boolean;
    disabled?: boolean;
}

const MARKUP_TYPES: IDropdownOption[] = [
    { key: PROMO_MARKUP_PERCENT, text: 'Percent markup' },
    { key: PROMO_MARKUP_GROSS_MARGIN, text: 'Gross margin' },
];

const MODE_MARKUP = 'markup';
const MODE_DISCOUNT = 'discount';
const MODE_INHERIT = 'inherit';

/**
 * The two shapes a promotion's pricing can take, as one control.
 *
 * They are presented as a choice rather than as one number because they are not the same quantity and
 * cannot be converted into one another without knowing the storefront's own markup. Setting a margin of
 * 20 says what the MSP keeps; taking 20% off says what the shopper saves. The discount is deliberately a
 * POSITIVE number: it used to be expressed as a negative markup, which prices from cost rather than from
 * the shopper's price and so quietly meant something else entirely.
 */
const PromoPricingFields: React.FC<IProps> = ({ markupType, markup, onChange, allowInherit, disabled }) => {
    // Null counts as unset: an override the API has never carried comes back as null, not absent.
    const inheriting = allowInherit && markupType == null && markup == null;
    const discounting = isDiscountPromo(markupType);
    const mode = inheriting ? MODE_INHERIT : (discounting ? MODE_DISCOUNT : MODE_MARKUP);

    const changeMode = (next?: string) => {
        if (next === MODE_INHERIT) { onChange(undefined, undefined); return; }
        // The number is carried across the switch rather than cleared. It is usually wrong for the new
        // mode and gets retyped, but silently emptying a field the user just filled in is worse than
        // showing a value they can see is wrong.
        if (next === MODE_DISCOUNT) { onChange(PROMO_MARKUP_DISCOUNT, markup); return; }
        onChange(PROMO_MARKUP_PERCENT, markup);
    };

    const options = [
        { key: MODE_MARKUP, text: 'Set the markup or margin' },
        { key: MODE_DISCOUNT, text: 'Discount off the normal price' },
        ...(allowInherit ? [{ key: MODE_INHERIT, text: 'Inherit from the promotion' }] : []),
    ];

    return (
        <Stack tokens={{ childrenGap: 8 }}>
            <ChoiceGroup
                label='Promotional pricing'
                selectedKey={mode}
                options={options}
                disabled={disabled}
                onChange={(_, option) => changeMode(option?.key)}
            />

            {mode === MODE_MARKUP && (
                <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign='end'>
                    <Dropdown
                        label='Type'
                        options={MARKUP_TYPES}
                        disabled={disabled}
                        selectedKey={markupType ?? PROMO_MARKUP_PERCENT}
                        onChange={(_, option) => onChange(option?.key as string, markup)}
                        styles={{ root: { minWidth: 180 } }}
                    />
                    <NumberField
                        label={markupType === PROMO_MARKUP_GROSS_MARGIN ? 'Margin %' : 'Markup %'}
                        decimal
                        readonly={disabled}
                        toolTip='The effective markup or margin while the promo runs, replacing the storefront’s usual one. The shopper always gets the lower of this and the normal price, so a value that would raise the price simply never applies.'
                        value={markup}
                        onChange={(value) => onChange(markupType ?? PROMO_MARKUP_PERCENT, value)}
                    />
                </Stack>
            )}

            {mode === MODE_DISCOUNT && (
                <NumberField
                    label='Discount %'
                    decimal
                    readonly={disabled}
                    toolTip='Taken off the price the shopper would otherwise pay, after the storefront’s own markup. A positive number: 15 means 15% off. Never goes below a product’s minimum price.'
                    value={markup}
                    onChange={(value) => onChange(PROMO_MARKUP_DISCOUNT, value)}
                />
            )}
        </Stack>
    );
};

export default PromoPricingFields;
